<template>
  <div class="newsletter-signup">
    <div
      v-if="!isTeaserPage"
      class="newsletter-signup__header"
    >
      <div
        class="newsletter-signup__header-copy"
        :role="isFooterForm ? 'status' : null"
      >
        <component
          :is="newsletterData.titleElement ? newsletterData.titleElement : 'h2'"
          class="newsletter-signup__title text-other-label-regular"
        >
          <span v-text="newsletterData.title" />
        </component>

        <p
          v-if="state === 'success'"
          v-text="$string('general.newsletter_signup.success_message')"
        />

        <p
          v-else
          v-text="newsletterData.copy"
        />
      </div>

      <div class="newsletter-signup__cancel-container">
        <button
          class="newsletter-signup__cancel-button button button--text"
          :class="{ 'newsletter-signup__cancel-button--visible': showCancelButton }"
          type="button"
          @click.prevent="toggleAdditionalFields"
          v-text="$string('general.newsletter_signup.cancel')"
        />
      </div>

      <p
        v-show="showAdditionalFields && !state === 'success'"
        v-text="$string('general.newsletter_signup.required.message')"
      />
    </div>

    <div
      v-if="state === 'success'"
      class="newsletter-signup__form"
    >
      <p
        v-if="isTeaserPage"
        v-text="$string('general.newsletter_signup.success_message')"
      />

      <button
        class="button button--secondary newsletter-signup__close"
        type="button"
        @click.prevent="resetForm"
        v-text="$string('general.newsletter_signup.close')"
      />
    </div>

    <form
      v-else
      ref="newsletterSignup"
      class="newsletter-signup__form form"
    >
      <div class="form-group newsletter-signup__email-row">
        <div
          class="form-group__field newsletter-signup__email"
          :class="{ 'newsletter-signup__email--with-inline-button': !isTeaserPage }"
        >
          <div class="floating-label">
            <input
              :id="`newsletter-signup-email-${namespace}`"
              ref="email"
              v-model="emailAddress"
              :aria-describedby="getAriaDescribedby('email')"
              :aria-invalid="getAriaInvalid('email')"
              class="form-group__input newsletter-signup__email-input"
              :class="{
                'newsletter-signup__email-input--is-teaser': isTeaserPage,
                [$class('error')]: state === 'error'
              }"
              name="email"
              required
              type="email"
              @keydown="handleEmailKeydown"
            >
            <label
              class="form-group__label"
              :for="`newsletter-signup-email-${namespace}`"
            >
              <span v-text="$string('general.newsletter_signup.email.placeholder')" />

              <abbr
                :title="$string('general.newsletter_signup.required.label')"
                v-text="$string('general.newsletter_signup.required.symbol')"
              />
            </label>
          </div>
          <input
            name="g"
            type="hidden"
            :value="$variable('settings.newsletter.marketingListId')"
          >

          <input
            v-if="newsletterData.customSource"
            type="hidden"
            name="$source"
            :value="newsletterData.customSource"
          >

          <button
            v-show="!isTeaserPage || isTeaserPage && !showAdditionalFields"
            class="newsletter-signup__email-button"
            :class="{
              'newsletter-signup__email-button--is-teaser button button--secondary': isTeaserPage
            }"
            aria-controls="newsletter-signup-additional-fields"
            :aria-expanded="showAdditionalFields"
            type="button"
            @click.prevent="toggleAdditionalFields"
          >
            <icon-chevron-right v-if="!isTeaserPage" />
            <span
              class="text-body-m-regular"
              v-text="$string('general.newsletter_signup.signup')"
            />
          </button>

          <button
            v-if="isTeaserPage && showAdditionalFields"
            class="
              newsletter-signup__cancel-button
              newsletter-signup__cancel-button--teaser
              button
              button--secondary
              text-body-m-regular
            "
            :class="{ 'newsletter-signup__cancel-button--visible': showCancelButton }"
            type="button"
            @click.prevent="toggleAdditionalFields"
            v-text="$string('general.newsletter_signup.cancel')"
          />
        </div>

        <p
          v-show="errorMessages.email"
          :id="`newsletter-signup-email-error-${namespace}`"
          class="newsletter-signup__error"
          v-text="errorMessages.email"
        />
      </div>

      <transition name="fade">
        <div
          v-show="showAdditionalFields"
          id="newsletter-signup-additional-fields"
          class="newsletter-signup__additional-fields"
        >
          <div class="form-group newsletter-signup__country-row">
            <label
              class="visually-hidden"
              :for="`newsletter-signup-country-${namespace}`"
            >
              <span v-text="$string('general.newsletter_signup.country.placeholder')" />

              <abbr
                :title="$string('general.newsletter_signup.required.label')"
                v-text="$string('general.newsletter_signup.required.symbol')"
              />
            </label>

            <select
              :id="`newsletter-signup-country-${namespace}`"
              ref="country"
              v-model="selectedCountry"
              :aria-describedby="getAriaDescribedby('country')"
              :aria-invalid="getAriaInvalid('country')"
              autocomplete="country-name"
              class="text-body-m-regular"
              name="country"
              required
            >
              <option
                disabled
                :value="null"
                v-text="getRequiredLabel($string('general.newsletter_signup.country.placeholder'))"
              />

              <option
                v-for="(country, index) of newsletterCountries"
                :key="index"
                :value="country"
                v-text="country"
              />
            </select>

            <p
              v-show="errorMessages.country"
              :id="`newsletter-signup-country-error-${namespace}`"
              class="newsletter-signup__error"
              v-text="errorMessages.country"
            />
          </div>

          <div class="form-group newsletter-signup__interests-row">
            <div
              role="group"
              :aria-labelledby="`newsletter-signup-interests-legend-${namespace}`"
              :aria-invalid="getAriaInvalid('interest')"
              :aria-describedby="getAriaDescribedby('interest')"
              aria-required="true"
              class="form-group__field newsletter-signup__interests"
            >
              <p
                :id="`newsletter-signup-interests-legend-${namespace}`"
                class="newsletter-signup__interests-legend text-body-m-regular"
                v-text="$string('general.newsletter_signup.interests.label')"
              />

              <checkbox-input
                :id="`newsletter-signup-interest-women-${namespace}`"
                ref="interest"
                v-model="interest.women"
                :label="$string('general.newsletter_signup.interests.women')"
                :model-value="false"
                name="interest"
                value="women"
              />

              <checkbox-input
                :id="`newsletter-signup-interest-men-${namespace}`"
                v-model="interest.men"
                :label="$string('general.newsletter_signup.interests.men')"
                :model-value="false"
                name="interest"
                value="men"
              />

              <checkbox-input
                :id="`newsletter-signup-interest-unisex-${namespace}`"
                v-model="interest.unisex"
                :label="$string('general.newsletter_signup.interests.unisex')"
                :model-value="false"
                name="interest"
                value="unisex"
              />
            </div>

            <p
              v-show="errorMessages.interests"
              :id="`newsletter-signup-interests-error-${namespace}`"
              class="newsletter-signup__error"
              v-text="errorMessages.interests"
            />
          </div>

          <div class="form-group">
            <div class="form-group__field">
              <checkbox-input
                :id="`newsletter-signup-consent-${namespace}`"
                ref="consent"
                v-model="hasConsent"
                :aria-invalid="getAriaInvalid('consent')"
                :label="getRequiredLabel($string('general.newsletter_signup.consent.consent_html'))"
                name="consent"
                :aria-describedby="hasError ? `newsletter-signup-consent-error-${namespace}` : null"
              />
            </div>

            <p
              v-show="errorMessages.consent"
              :id="`newsletter-signup-consent-error-${namespace}`"
              class="newsletter-signup__error"
              v-text="errorMessages.consent"
            />
          </div>

          <div class="form-group">
            <button
              class="button button--primary text-other-button-l"
              type="submit"
              @click.prevent="handleFormSubmit"
              v-text="$string('general.newsletter_signup.submit')"
            />
          </div>
        </div>
      </transition>
    </form>
  </div>
</template>

<script>

/**
 * Vue: Newsletter signup (newsletter-signup)
 * -----------------------------------------------------------------------------
 * Form containing fields for email, country and fashion interests that sends
 * data to Magento and Splio.
 *
 * @param {Boolean} [isTeaserPage] - If rendered on the teaser page.
 * @param {String} namespace - Unique namespace.
 * @param {Object} newsletterData - Object containing copy & countries for form.
 *
 */
import subscribe from '~/apis/klaviyo'
import { formatKlaviyoSubscriptionData } from '~/helpers/format'
import { isEmailValid } from '~/helpers/general'

import CheckboxInput from '~async/utils/checkbox-input/checkbox-input'
import IconChevronRight from '~icons/directional-navigation/chevron/right.svg'

export default {
  name: 'NewsletterSignup',

  components: {
    CheckboxInput,
    IconChevronRight,
  },

  props: {
    isTeaserPage: {
      type: Boolean,
      default: false,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    newsletterData: {
      type: Object,
      default: () => ({
        copy: '',
        title: '',
        titleElement: '',
      }),
    },
  },

  data() {
    return {
      emailAddress: '',
      errorMessages: {
        consent: '',
        country: '',
        email: '',
        interests: '',
      },
      hasConsent: false,
      hasSubmitted: false,
      interest: {
        men: false,
        women: false,
        unisex: false,
      },
      selectedCountry: null,
      showAdditionalFields: false,
      state: 'ready',
      hasError: false,
    }
  },

  computed: {

    /**
     * Return if a country was selected.
     * @returns {Boolean}
     */
    countryIsValid() {
      return Boolean(this.selectedCountry)
    },

    /**
     * Return if at least one interest checkbox was checked.
     * @returns {Boolean}
     */
    hasInterest() {
      return this.interest.men || this.interest.women || this.interest.unisex
    },

    /**
     * Compute if this is the footer form.
     * @returns {Boolean}
     */
    isFooterForm() {
      return this.namespace === 'site-footer'
    },

    /**
     * Return list of newsletter countries.
     * @returns {Boolean}
     */
    newsletterCountries() {
      return this.$variable('settings.newsletter.countries')
    },

    /**
     * Return whether to show the cancel button.
     * @returns {Boolean}
     */
    showCancelButton() {
      return this.showAdditionalFields && this.state !== 'success'
    },
  },

  methods: {

    /**
     * Return the id of the error message to populate the appropriate
     * `aria-describedby` attribute.
     * - Set to `null` by default, and if no error message is present to prevent
     *   the attribute from appearing and pointing to a non-existant element.
     * @param {String} field - Type of input field. Accepts 'consent', 'country'
     * or 'email'.
     * @returns {String|null}
     */
    getAriaDescribedby(field) {
      if (!this.hasSubmitted) {
        return null
      }

      let ariaDescribedby = null

      switch (field) {
        case 'consent':
          ariaDescribedby = this.hasConsent
            ? null
            : `newsletter-signup-consent-error-${this.namespace}`
          break
        case 'country':
          ariaDescribedby = this.countryIsValid
            ? null
            : `newsletter-signup-country-error-${this.namespace}`
          break
        case 'interest':
          ariaDescribedby = this.hasInterest
            ? null
            : `newsletter-signup-interests-error-${this.namespace}`
          break
        case 'email':
          ariaDescribedby = isEmailValid(this.emailAddress)
            ? null
            : `newsletter-signup-email-error-${this.namespace}`
          break
      }

      return ariaDescribedby
    },

    /**
     * Set `aria-invalid` attribute.
     * - Set to `null` by default to prevent the attribute from appearing on
     *   the element (mirrors similar Liquid implementations).
     * @param {String} field - Type of input field. Accepts 'consent', 'country'
     * or 'email'.
     * @returns {Boolean|null}
     */
    getAriaInvalid(field) {
      if (!this.hasSubmitted) {
        return null
      }

      let isInvalid = null

      switch (field) {
        case 'consent':
          isInvalid = !this.hasConsent
          break
        case 'country':
          isInvalid = !this.countryIsValid
          break
        case 'interest':
          isInvalid = !this.hasInterest
          break
        case 'email':
          isInvalid = !isEmailValid(this.emailAddress)
          break
      }

      return isInvalid
    },

    /**
     * Get label/placeholder for required fields that includes required symbol.
     * @param {String} label - Original translation string
     * @returns {String}
     */
    getRequiredLabel(label) {
      return `${label}${this.$string('general.newsletter_signup.required.symbol')}`
    },

    /**
     * Overrides default form behaviour so that pressing the 'Enter' key while
     * focused within the email input does not attempt to submit the form.
     * Instead, it will display the hidden form fields.
     * @param {Object} event - Input event.
     */
    handleEmailKeydown(event) {
      if (event.key !== 'Enter') {
        return
      }

      event.preventDefault()
      this.toggleAdditionalFields()
    },

    /**
     * Handle events on newsletter signup form submission.
     * - Prevent form from submitting if any errors are present, and set error
     *   messages.
     */
    handleFormSubmit() {
      this.hasSubmitted = true
      this.resetErrorMessages()
      this.state = 'ready'

      let fieldFocused = false

      if (!isEmailValid(this.emailAddress)) {
        this.state = 'error'
        this.errorMessages.email = this.$string('general.newsletter_signup.email.error')

        this.$refs.email.focus()
        fieldFocused = true
      }

      if (!this.countryIsValid) {
        this.state = 'error'
        this.errorMessages.country = this.$string('general.newsletter_signup.country.error')

        if (!fieldFocused) {
          this.$refs.country.focus()
          fieldFocused = true
        }
      }

      if (!this.hasInterest) {
        this.state = 'error'
        this.errorMessages.interests = this.$string('general.newsletter_signup.interests.error')

        if (!fieldFocused) {
          this.$refs.interest.focus()
          fieldFocused = true
        }
      }

      if (!this.hasConsent) {
        this.state = 'error'
        this.hasError = true
        this.errorMessages.consent = this.$string('general.newsletter_signup.consent.error')

        if (!fieldFocused) {
          this.$refs.consent.focus()
          fieldFocused = true
        }
      }

      if (this.state === 'error') {
        return
      }

      const data = formatKlaviyoSubscriptionData(new FormData(this.$refs.newsletterSignup))

      subscribe(data)
        .then(() => {
          this.state = 'success'
        })
        .catch(() => {
          this.state = 'error'
          this.emailError =
            this.$string('general.newsletter_signup.error_generic')
        })
    },

    /**
     * Reset error messages to default (blank string).
     */
    resetErrorMessages() {
      this.errorMessages.consent = ''
      this.errorMessages.country = ''
      this.errorMessages.email = ''
      this.errorMessages.interests = ''
    },

    /**
     * Reset form state.
     */
    resetForm() {
      this.emailAddress = ''
      this.hasConsent = false
      this.interest.men = false
      this.interest.women = false
      this.interest.unisex = false
      this.selectedCountry = false
      this.showAdditionalFields = false
      this.state = 'ready'
    },

    /**
     * Toggle additional fields of form.
     */
    toggleAdditionalFields() {
      this.showAdditionalFields = !this.showAdditionalFields
    },
  },
}
</script>

<style lang="scss">
@import './newsletter-signup';
</style>
